import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, List, ListItem, ListItemText, ListItemAvatar, Avatar, FormControl, TextField, Divider, FormGroup, FormControlLabel, Checkbox } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import DescriptionIcon from '@material-ui/icons/Description';
import ViewColumnIcon from '@material-ui/icons/ViewColumn';
import debounce from 'lodash/debounce';
import { translations } from '../../i18n/i18n';
import { makeStyles } from '@material-ui/styles';

const searchPlaces = (places, searchValue) => {
	if(searchValue == '') {
		return places;
	}

	//Apply array filter
	return places.filter(place => {
		if(place.name === null) place.name = '';
		if(place.location === null) place.location = '';
		if(!(place.name.toLowerCase().includes(searchValue.toLowerCase()) || place.location.toLowerCase().includes(searchValue.toLowerCase()))) {
			return false;
		}

		return true;
	});
};

const filterPlaces = (places, placeTypeFilter, contentFilter) => {
	return places.filter(place => {
		//Place type filter
		if(place.place_type_ids.filter(v => placeTypeFilter.includes(v)).length === 0) {
			return false;
		}

		//Content filter
		if(contentFilter.includes('photo') && place.number_of_photos === 0) {
			return false;
		}
		if(contentFilter.includes('video') && place.number_of_videos === 0) {
			return false;
		}
		if(contentFilter.includes('guide') && place.has_guide === false) {
			return false;
		}

		return true;
	});
};

const useSeparatorStyles = makeStyles((theme) => ({
	root: {
		marginTop: theme.spacing(2.3),
		marginBottom: theme.spacing(0.5),

		'@media (min-height: 1000px)': {
			marginTop: theme.spacing(4),
			marginBottom: theme.spacing(2),
		},
	}
}));
const Separator = () => {
	const classes = useSeparatorStyles();

	return <li className={classes.root}>
		<Divider />
	</li>;
};


const MapFilter = ({ placeTypes, places, setPlacesToShow }) => {
	const [searchValue, setSearchValue] = useState('');
	const [placeTypeFilter, setPlaceTypeFilter] = useState(placeTypes.map(placeType => placeType.id));
	const [contentFilter, setContentFilter] = useState([]);
	const filteredPlacesRef = useRef(places);

	const searchDebounceHandler = useCallback(debounce((text) => {
		const filteredPlacesWithSearch = searchPlaces(filteredPlacesRef.current, text);

		setPlacesToShow(filteredPlacesWithSearch);
	}, 400), []);

	useEffect(() => {
		filteredPlacesRef.current = filterPlaces(places, placeTypeFilter, contentFilter);
		const filteredPlacesWithSearch = searchPlaces(filteredPlacesRef.current, searchValue);

		setPlacesToShow(filteredPlacesWithSearch);
	}, [places, placeTypeFilter, contentFilter]);

	const handlePlaceTypeCheckboxChange = (placeTypeId) => {
		if(placeTypeFilter.includes(placeTypeId)) {
			setPlaceTypeFilter(placeTypeFilter.filter(id => id !== placeTypeId));
		} else {
			setPlaceTypeFilter([...placeTypeFilter, placeTypeId]);
		}
	};
	const handleContentFilterCheckboxChange = (key) => {
		if(contentFilter.includes(key)) {
			setContentFilter(contentFilter.filter(id => id !== key));
		} else {
			setContentFilter([...contentFilter, key]);
		}
	};
	const handleSearchInputChange = (e) => {
		setSearchValue(e.target.value);
		searchDebounceHandler(e.target.value);
	};

	return <List>
		<ListItem>
			<ListItemAvatar>
				<Avatar classes={{ root: 'filters-avatar' }}>
					<SearchIcon />
				</Avatar>
			</ListItemAvatar>
			<ListItemText primary={translations.components.mapFilter.searchPrimary} secondary={translations.components.mapFilter.searchSecondary} />
		</ListItem>
		<Box component="li" display="grid" ml={2} mr={4} paddingLeft="56px" gridTemplateColumns="1fr">
			<FormControl fullWidth>
				<TextField
					value={searchValue}
					onChange={handleSearchInputChange}
					fullWidth
					color="secondary"
					variant="standard"
					size="small"
					inputProps={{
						placeholder: translations.components.mapFilter.searchPlaceholder,
					}} />
			</FormControl>
		</Box>

		<Separator />

		<ListItem>
			<ListItemAvatar>
			<Avatar classes={{ root: 'filters-avatar' }}>
				<ViewColumnIcon />
			</Avatar>
			</ListItemAvatar>
			<ListItemText primary={translations.components.mapFilter.filterPrimary} secondary={translations.components.mapFilter.filterSecondary} />
		</ListItem>
		<Box component="li" display="grid" mx={2} paddingLeft="56px" gridTemplateColumns="1fr 1fr" className="filters-grid">
			{placeTypes.map(placeType => <FormControlLabel
				key={placeType.id}
				control={<Checkbox size="small" color="default" checked={placeTypeFilter.includes(placeType.id)} onChange={() => handlePlaceTypeCheckboxChange(placeType.id)} />}
				label={placeType.name}
			/>)}
		</Box>

		<Separator />

		<ListItem>
			<ListItemAvatar>
			<Avatar classes={{ root: 'filters-avatar' }}>
				<DescriptionIcon />
			</Avatar>
			</ListItemAvatar>
			<ListItemText primary={translations.components.mapFilter.dataPrimary} secondary={translations.components.mapFilter.dataSecondary} />
		</ListItem>
		<Box component="li" display="grid" mx={2} paddingLeft="56px" gridTemplateColumns="1fr 1fr" className="filters-grid">
			<FormGroup>
				<FormControlLabel
					control={<Checkbox size="small" color="default" checked={contentFilter.includes("photo")} onChange={() => handleContentFilterCheckboxChange("photo")} />}
					label={translations.global.photo}
				/>
				<FormControlLabel
					control={<Checkbox size="small" color="default" checked={contentFilter.includes("video")} onChange={() => handleContentFilterCheckboxChange("video")} />}
					label={translations.global.video}
				/>
			</FormGroup>
			<FormGroup>
				{/*<FormControlLabel
					control={<Checkbox size="small" color="default" checked={contentFilter.includes("model")} onChange={() => handleContentFilterCheckboxChange("model")} />}
					label="3D rekonstrukció"
				/>*/}
				<FormControlLabel
					control={<Checkbox size="small" color="default" checked={contentFilter.includes("guide")} onChange={() => handleContentFilterCheckboxChange("guide")} />}
					label={translations.global.guide}
				/>
			</FormGroup>
		</Box>

		<Separator />
	</List>;
}

export default React.memo(MapFilter);